import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Loader } from './Loader';
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";

const BOUNDS_PADDING = 48;
const DEFAULT_ZOOM = 13;
const SINGLE_VENUE_ZOOM = 15;

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%", borderRadius: "0.5rem" };

// Marker colors by venue type (fill, stroke)
const MARKER_COLORS: Record<string, [string, string]> = {
  restaurant: ["#22c55e", "#15803d"],
  bar: ["#a855f7", "#6d28d9"],
  cafe: ["#d97706", "#92400e"],
};
const DEFAULT_COLORS: [string, string] = ["#64748b", "#475569"];

// Avoid centering on null island; use NYC when no valid coords
const DEFAULT_CENTER = { lat: 40.7128, lng: -74.006 };
function isValidCoord(lat: number, lng: number): boolean {
  return (
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    (Math.abs(lat) > 1e-5 || Math.abs(lng) > 1e-5)
  );
}

function getMarkerIconUrl(type?: string): string {
  const [fill, stroke] = (type && MARKER_COLORS[type]) || DEFAULT_COLORS;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="${fill}" stroke="${stroke}" stroke-width="2"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

interface Venue {
  id: string;
  name: string;
  address?: string | null;
  type?: string | null;
  lat?: number | null;
  lng?: number | null;
  phone?: string | null;
  website?: string | null;
  googleMapsLink?: string | null;
  description?: string | null;
  photoUrl?: string | null;
  rating?: number | null;
  userRatingCount?: number | null;
  priceLevel?: string | null;
  tags?: string[] | null;
}

export type MapBounds = { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } };

interface OverallVenuesMapProps {
  venues: Venue[];
  className?: string;
  height?: string;
  /** Called when the map is ready; getBounds() returns current viewport bounds or null */
  onMapReady?: (getBounds: () => MapBounds | null) => void;
}

export default function OverallVenuesMap({
  venues,
  className = "",
  height = "32rem",
  onMapReady,
}: OverallVenuesMapProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-venues",
    googleMapsApiKey: apiKey || "",
  });

  const venuesWithCoords = useMemo(
    () =>
      venues.filter((v) => {
        if (v.lat == null || v.lng == null) return false;
        const lat = Number(v.lat);
        const lng = Number(v.lng);
        return isValidCoord(lat, lng);
      }),
    [venues]
  );

  const center = useMemo(() => {
    if (venuesWithCoords.length === 0) return DEFAULT_CENTER;
    const lat =
      venuesWithCoords.reduce((s, v) => s + Number(v.lat), 0) / venuesWithCoords.length;
    const lng =
      venuesWithCoords.reduce((s, v) => s + Number(v.lng), 0) / venuesWithCoords.length;
    return isValidCoord(lat, lng) ? { lat, lng } : DEFAULT_CENTER;
  }, [venuesWithCoords]);

  // Never pass 0/0 to the map (library may use it as initial view and not update)
  const safeCenter = useMemo(
    () => (isValidCoord(center.lat, center.lng) ? center : DEFAULT_CENTER),
    [center]
  );

  // Fit map to include all venue coordinates (with padding)
  const fitMapToVenues = useCallback(() => {
    const map = mapRef.current;
    if (!map || venuesWithCoords.length === 0) return;
    if (venuesWithCoords.length === 1) {
      const v = venuesWithCoords[0];
      map.setCenter({ lat: Number(v.lat), lng: Number(v.lng) });
      map.setZoom(SINGLE_VENUE_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const v of venuesWithCoords) {
      bounds.extend({ lat: Number(v.lat), lng: Number(v.lng) });
    }
    map.fitBounds(bounds, BOUNDS_PADDING);
  }, [venuesWithCoords]);

  useEffect(() => {
    fitMapToVenues();
  }, [fitMapToVenues]);

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      fitMapToVenues();
      onMapReady?.(() => {
        const b = map.getBounds();
        if (!b) return null;
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        return {
          ne: { lat: ne.lat(), lng: ne.lng() },
          sw: { lat: sw.lat(), lng: sw.lng() },
        };
      });
    },
    [fitMapToVenues, onMapReady]
  );

  const cancelHoverClose = useCallback(() => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout]);

  const handleMouseOver = useCallback(
    (id: string) => {
      cancelHoverClose();
      setHoveredId(id);
    },
    [cancelHoverClose]
  );

  const scheduleHoverClose = useCallback(() => {
    const t = setTimeout(() => setHoveredId(null), 400);
    setHoverTimeout(t);
  }, []);

  const handleMouseOut = useCallback(() => {
    scheduleHoverClose();
  }, [scheduleHoverClose]);

  // Fallback: no API key or load error → use iframe embed
  const fallbackUrl = useMemo(
    () =>
      `https://www.google.com/maps?q=${safeCenter.lat},${safeCenter.lng}&output=embed`,
    [safeCenter]
  );

  // When no venues have valid coords, still show map at default center (avoid 0/0)
  if (venuesWithCoords.length === 0 && venues.length === 0) return null;

  const useFallback = (!apiKey || loadError) && !!fallbackUrl;

  if (useFallback) {
    return (
      <div className={className} style={{ height }}>
        <iframe
          src={fallbackUrl}
          className="w-full h-full border-0 rounded-lg"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Venues map"
        />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`flex items-center justify-center bg-dark-border/30 rounded-lg ${className}`}
        style={{ height }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div className={className} style={{ height }}>
      <GoogleMap
        key={`${safeCenter.lat},${safeCenter.lng}`}
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={safeCenter}
        zoom={DEFAULT_ZOOM}
        onLoad={handleMapLoad}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          fullscreenControl: true,
          streetViewControl: false,
        }}
      >
        {venuesWithCoords.map((venue) => (
          <Marker
            key={venue.id}
            position={{ lat: Number(venue.lat), lng: Number(venue.lng) }}
            icon={
              typeof window !== "undefined" && window.google?.maps
                ? { url: getMarkerIconUrl(venue.type ?? undefined), scaledSize: new window.google.maps.Size(24, 24) }
                : undefined
            }
            onMouseOver={() => handleMouseOver(venue.id)}
            onMouseOut={handleMouseOut}
            cursor="pointer"
          />
        ))}
        {hoveredId && (() => {
          const venue = venuesWithCoords.find((v) => v.id === hoveredId);
          if (!venue) return null;
          // Offset the InfoWindow down so it sits below the marker and doesn't cover it (avoids hover flicker)
          const pixelOffset =
            typeof window !== "undefined" && window.google?.maps
              ? new window.google.maps.Size(0, -28)
              : undefined;
          return (
            <InfoWindow
              position={{ lat: Number(venue.lat), lng: Number(venue.lng) }}
              onCloseClick={() => setHoveredId(null)}
              options={pixelOffset ? { pixelOffset } : undefined}
            >
              <div
                className="min-w-[220px] max-w-[300px] text-left"
                onMouseEnter={cancelHoverClose}
                onMouseLeave={scheduleHoverClose}
              >
                {venue.photoUrl && (
                  <img
                    src={venue.photoUrl}
                    alt={venue.name}
                    className="w-full h-32 object-cover rounded-t -mt-1 -mx-1 mb-1"
                    loading="lazy"
                  />
                )}
                <div className="px-1 pb-1">
                  <div className="font-semibold text-sm text-gray-900">{venue.name}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-700">
                    {venue.type && (
                      <span className="px-1.5 py-0.5 rounded bg-gray-200 capitalize">
                        {venue.type}
                      </span>
                    )}
                    {venue.rating != null && (
                      <span>
                        ⭐ {venue.rating.toFixed(1)}
                        {venue.userRatingCount != null && (
                          <span className="text-gray-500"> ({venue.userRatingCount})</span>
                        )}
                      </span>
                    )}
                    {venue.priceLevel && <span>{venue.priceLevel}</span>}
                  </div>
                  {venue.description && (
                    <div className="mt-1 text-xs text-gray-700 italic line-clamp-3">
                      {venue.description}
                    </div>
                  )}
                  {venue.address && (
                    <div className="mt-1 text-xs text-gray-600">{venue.address}</div>
                  )}
                  {venue.phone && (
                    <div className="mt-0.5 text-xs text-gray-600">📞 {venue.phone}</div>
                  )}
                  {venue.tags && venue.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {venue.tags.slice(0, 6).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-600"
                        >
                          {t.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-1 flex flex-wrap gap-3">
                    {venue.website && (
                      <a
                        href={venue.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Website →
                      </a>
                    )}
                    {venue.googleMapsLink && (
                      <a
                        href={venue.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Google Maps →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </InfoWindow>
          );
        })()}
      </GoogleMap>
    </div>
  );
}
